import { ChangeEvent, DragEvent, useRef, useState } from 'react';

const MAX_FILE_SIZE = 18 * 1024 * 1024; // 18 Mo

const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.heic'];

function isValidContractFile(file: File): { valid: boolean; error?: string } {
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'Le fichier est trop volumineux (18 Mo maximum).' };
  }

  const nameLower = file.name.toLowerCase();
  const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) => nameLower.endsWith(ext));
  const hasValidMime = file.type === 'application/pdf' || file.type.startsWith('image/');

  if (!hasValidExtension && !hasValidMime) {
    return {
      valid: false,
      error: 'Format non pris en charge. Veuillez sélectionner un document PDF ou une image (PNG, JPEG, WebP).',
    };
  }

  return { valid: true };
}

type Props = {
  onFileSelect: (file: File) => void;
};

export function useContractAiDropzone(props: Props) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function processFile(file: File) {
    setValidationError(null);
    const check = isValidContractFile(file);
    if (!check.valid) {
      setValidationError(check.error ?? 'Fichier non valide.');
      return;
    }
    props.onFileSelect(file);
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragOver(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  }

  function handleFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  }

  function handleTriggerFileInput() {
    fileInputRef.current?.click();
  }

  return {
    isDragOver,
    validationError,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInputChange,
    handleTriggerFileInput,
  };
}
